import { useState, useCallback, useEffect, useRef } from "react";
import { remixAvatar as remixAvatarUtil } from "../../../utils/avatar-utils";
import { proxiedUrlFor } from "../../../utils/media-url-utils";
import notificationsCenter from "../../../singleton/NotificationsCenter";
import { useIntl } from "react-intl";
import { useWorkspaceContext } from "../../contexts/WorkspaceContext";
import configs from "../../../utils/configs";
import { getAuthHeaders } from "../../../utils/getToken";
import { deletePersistenceAvatar } from "../../../utils/avatar-deletion";

export function useManageAvatars(isSignedIn, initialFilter = "my-avatars") {
  const intl = useIntl();
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursors, setPrevCursors] = useState([]);

  const hasNextPage = Boolean(nextCursor);
  const hasPrevPage = prevCursors.length > 0;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
      if (query !== debouncedQuery) {
        setCursor(null);
        setPrevCursors([]);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [query, debouncedQuery]);

  const { getDefaultWorkspace } = useWorkspaceContext();

  const lastFetchParamsRef = useRef({ cursor: null, filter: null, query: null, workspaceId: null });

  const fetchAvatarsInternal = useCallback(
    async (currentCursor, currentFilter, currentDebouncedQuery) => {
      const currentWorkspaceId = getDefaultWorkspace()?.id || null;

      // Guard: Don't re-fetch if parameters are identical to last fetch
      if (
        lastFetchParamsRef.current.cursor === currentCursor &&
        lastFetchParamsRef.current.filter === currentFilter &&
        lastFetchParamsRef.current.query === currentDebouncedQuery &&
        lastFetchParamsRef.current.workspaceId === currentWorkspaceId
      ) {
        return;
      }

      lastFetchParamsRef.current = {
        cursor: currentCursor,
        filter: currentFilter,
        query: currentDebouncedQuery,
        workspaceId: currentWorkspaceId
      };

      setLoading(true);
      setError(null);
      try {
        if (currentFilter === "featured" || currentFilter === "my-avatars") {
          if (currentFilter === "featured") {
            const queryParams = new URLSearchParams();
            queryParams.append("page", String(currentCursor || 1));
            queryParams.append("per_page", "12");
            queryParams.append("is_featured", "true");

            if (currentDebouncedQuery) {
              queryParams.append("search", currentDebouncedQuery);
            }

            const response = await fetch(`${configs.PERSISTENCE_API_URL}/avatars?${queryParams.toString()}`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                accept: "application/json"
              }
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch avatars: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            const entries = (data.data || []).map(avatar => ({
              ...avatar,
              id: avatar.sid || avatar.id,
              avatar_id: avatar.sid || avatar.id,
              uuid: avatar.id,
              type: "avatar",
              name: avatar.name,
              asset_url: proxiedUrlFor(avatar.url),
              gltfs: {
                avatar: proxiedUrlFor(avatar.url)
              },
              images: {
                preview: {
                  url: avatar.thumbnail,
                  width: 1080,
                  height: 1920
                }
              },
              workspace_id: avatar.workspace_id
            }));
            setAvatars(entries);

            let next = null;
            if (data.meta) {
              const { current_page, to, total } = data.meta;
              if (to < total) {
                next = current_page + 1;
              }
            }
            setNextCursor(next);
            return;
          }

          const canViewAssets = workspace =>
            workspace?.authorization?.projection_status === "ready" &&
            Array.isArray(workspace.authorization.permissions) &&
            workspace.authorization.permissions.includes("asset:read");
          const effectiveWorkspace = getDefaultWorkspace();

          if (!effectiveWorkspace?.id || !canViewAssets(effectiveWorkspace)) {
            setNextCursor(null);
            setAvatars([]);
            setLoading(false);
            return;
          }

          const apiUrl = `${configs.PERSISTENCE_API_URL}/workspaces/${effectiveWorkspace.id}/avatars`;
          const queryParams = new URLSearchParams();

          if (currentDebouncedQuery) {
            queryParams.append("search", currentDebouncedQuery);
          }

          if (currentCursor) {
            queryParams.append("page", currentCursor);
          }

          queryParams.append("per_page", "10");

          const url = `${apiUrl}?${queryParams.toString()}`;
          const authHeaders = await getAuthHeaders(url, "GET");
          if (!authHeaders.Authorization) {
            setAvatars([]);
            setNextCursor(null);
            setLoading(false);
            return;
          }

          const headers = {
            "Content-Type": "application/json",
            accept: "application/json",
            ...authHeaders
          };

          const response = await fetch(url, {
            method: "GET",
            headers
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch avatars: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();

          // Transform Persistence API response to match Reticulum structure
          const entries = (data.data || []).map(avatar => ({
            ...avatar,
            id: avatar.sid,
            avatar_id: avatar.sid, // For compatibility
            uuid: avatar.id, // Keep internal ID if needed
            type: "avatar",
            name: avatar.name,
            asset_url: proxiedUrlFor(avatar.url),
            gltfs: {
              avatar: proxiedUrlFor(avatar.url)
            },
            images: {
              preview: {
                url: avatar.thumbnail,
                width: 1080,
                height: 1920
              }
            },
            workspace_id: avatar.workspace_id
          }));

          setAvatars(entries);

          // Extract page/cursor from meta or links
          let next = null;
          if (data.meta) {
            const { current_page, to, total } = data.meta;
            if (to < total) {
              next = current_page + 1;
            }
          }
          setNextCursor(next);
        }
      } catch (err) {
        console.error("Failed to fetch avatars:", err);
        setError(err.message || "Failed to fetch avatars");
        setAvatars([]);
        setNextCursor(null);
      } finally {
        setLoading(false);
      }
    },
    [getDefaultWorkspace]
  );

  useEffect(() => {
    fetchAvatarsInternal(cursor, filter, debouncedQuery);
  }, [cursor, filter, debouncedQuery, fetchAvatarsInternal]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      setPrevCursors(prev => [...prev, cursor]);
      setCursor(nextCursor);
    }
  }, [hasNextPage, nextCursor, cursor]);

  const goToPrevPage = useCallback(() => {
    if (hasPrevPage) {
      setCursor(prevCursors[prevCursors.length - 1] || null);
      setPrevCursors(prev => prev.slice(0, -1));
    }
  }, [hasPrevPage, prevCursors]);

  const changeFilter = useCallback(newFilter => {
    setFilter(newFilter);
    setCursor(null);
    setPrevCursors([]);
  }, []);

  const refreshAvatars = useCallback(() => {
    // This will trigger the useEffect above by changing its dependencies if needed,
    // or directly call fetch if the current page is 1.
    fetchAvatarsInternal(cursor, filter, debouncedQuery);
  }, [filter, debouncedQuery, cursor, fetchAvatarsInternal]);

  const renameAvatarAPI = useCallback(
    async (avatarId, newName) => {
      const avatar = avatars.find(a => (a.id || a.avatar_id) === avatarId);

      try {
        if (avatar?.uuid && avatar?.workspace_id) {
          // Persistence API
          const url = `${configs.PERSISTENCE_API_URL}/workspaces/${avatar.workspace_id}/avatars/${avatar.id}`;
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              ...(await getAuthHeaders(url, "PATCH"))
            },
            body: JSON.stringify({ name: newName, sid: avatar.sid })
          });

          if (!response.ok) {
            throw new Error(`Persistence API rename failed: ${response.status}`);
          }

          // Optimistic update: update the name in the current list
          setAvatars(prev => prev.map(a => ((a.id || a.avatar_id) === avatarId ? { ...a, name: newName } : a)));
        } else {
          throw new Error("Cannot rename: Invalid avatar data (missing uuid/workspace_id)");
        }

        notificationsCenter.success(
          intl.formatMessage({
            id: "media-browser.rename-avatar-success",
            defaultMessage: "Avatar renamed successfully."
          })
        );
        refreshAvatars();
      } catch (err) {
        console.error("Failed to rename avatar:", err);
        notificationsCenter.error(
          intl.formatMessage({
            id: "avatar-editor.error.rename-failed",
            defaultMessage: "Failed to rename avatar."
          })
        );
      }
    },
    [intl, refreshAvatars, avatars]
  );

  const deleteAvatarAPI = useCallback(
    async avatarId => {
      const avatar = avatars.find(a => (a.id || a.avatar_id) === avatarId);

      try {
        const result = await deletePersistenceAvatar({
          avatar,
          persistenceApiUrl: configs.PERSISTENCE_API_URL,
          authHeaders: getAuthHeaders
        });

        notificationsCenter.success(
          intl.formatMessage({
            id: "media-browser.delete-avatar-success",
            defaultMessage: "Avatar deleted successfully."
          })
        );
        // Optimistic update: Remove from list immediately
        setAvatars(prev => prev.filter(a => (a.id || a.avatar_id) !== avatarId));
        // refreshAvatars(); // Disabled to prevent eventual consistency race conditions
        return result;
      } catch (err) {
        console.error("Failed to delete avatar:", err);
        notificationsCenter.error(
          intl.formatMessage({
            id: "avatar-editor.error.delete-failed",
            defaultMessage: "Failed to delete avatar."
          })
        );
        throw err;
      }
    },
    [intl, avatars]
  );

  const remixAvatarAPI = useCallback(
    async (templateAvatarId, newName) => {
      const doRemix = async () => {
        try {
          await remixAvatarUtil(templateAvatarId, newName);
          notificationsCenter.success(
            intl.formatMessage({
              id: "avatar-editor.remix-success",
              defaultMessage: "Avatar copied successfully."
            })
          );
          if (filter !== "my-avatars") {
            setFilter("my-avatars"); // Triggers re-fetch
          } else {
            refreshAvatars();
          }
        } catch (err) {
          console.error("Failed to remix avatar:", err);
          notificationsCenter.error(
            intl.formatMessage({
              id: "avatar-editor.error.remix-failed",
              defaultMessage: "Failed to remix avatar."
            })
          );
        }
      };

      await doRemix();
    },
    [intl, filter, refreshAvatars]
  );

  return {
    avatars,
    loading,
    error,
    query,
    setQuery,
    filter,
    changeFilter,
    hasNextPage,
    hasPrevPage,
    goToNextPage,
    goToPrevPage,
    renameAvatarAPI,
    deleteAvatarAPI,
    remixAvatarAPI,
    refreshAvatars
  };
}
